import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Instagram, Globe, Calendar, MapPin, Ticket, UserPlus, Check, Loader2, Megaphone, Share2, Mail, Facebook, Twitter, MessageCircle, Copy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PromoterProfilePage() {
  const { handle } = useParams();
  const { token, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [followEmail, setFollowEmail] = useState('');
  const [working, setWorking] = useState(false);
  const [emailFollowed, setEmailFollowed] = useState(false);

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/promo/profiles/${handle}`, { headers: authHeaders });
        setData(res.data);
      } catch { setData(null); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handle, token]);

  const toggleFollow = async () => {
    setWorking(true);
    try {
      if (user && data?.is_following) {
        const res = await axios.post(`${API}/promo/profiles/${handle}/unfollow`, {}, { headers: authHeaders });
        setData(d => ({ ...d, is_following: false, followers_count: res.data.followers_count }));
      } else if (user) {
        const res = await axios.post(`${API}/promo/profiles/${handle}/follow`, {}, { headers: authHeaders });
        setData(d => ({ ...d, is_following: true, followers_count: res.data.followers_count }));
        toast.success(`Following ${data?.profile?.display_name} — you'll get an email when they promote a new event`);
      } else {
        if (!followEmail.includes('@')) { toast.error('Enter a valid email'); return; }
        const res = await axios.post(`${API}/promo/profiles/${handle}/follow`, { email: followEmail });
        setData(d => ({ ...d, followers_count: res.data.followers_count }));
        setEmailFollowed(true);
        toast.success("You're in — we'll email you when they promote a new event");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not follow');
    } finally { setWorking(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!data) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3 px-6 text-center">
      <Megaphone className="h-10 w-10 text-muted-foreground" />
      <h1 className="text-xl font-bold">Promoter not found</h1>
      <p className="text-sm text-muted-foreground">This promoter page doesn't exist or isn't public.</p>
      <Link to="/events" className="text-sm text-primary underline">Browse events instead</Link>
    </div>
  );

  const p = data.profile;
  const socials = p.socials || {};
  const pageUrl = `${window.location.origin}/promo/${p.handle}`;
  const shareText = `Follow ${p.display_name} on Eventism — every event they promote, in one place: ${pageUrl}`;
  const sharePage = async () => {
    try {
      if (navigator.share) { await navigator.share({ title: `${p.display_name} on Eventism`, text: shareText, url: pageUrl }); return; }
    } catch { return; }
    try { await navigator.clipboard.writeText(pageUrl); toast.success('Link copied'); } catch { toast.info(pageUrl); }
  };
  const copyPage = async () => {
    try { await navigator.clipboard.writeText(pageUrl); toast.success('Link copied'); } catch { toast.info(pageUrl); }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="promoter-profile-page">
      <Helmet>
        <title>{`${p.display_name} — Promoter on Eventism`}</title>
        <meta name="description" content={p.bio || `Follow ${p.display_name} on Eventism to catch every event they promote.`} />
      </Helmet>

      {/* Cover */}
      <div className="h-44 sm:h-60 w-full bg-gradient-to-r from-indigo-950 via-slate-900 to-emerald-950 relative overflow-hidden">
        {p.cover_url && <img src={p.cover_url} alt="" className="w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />
      </div>

      <div className="max-w-3xl mx-auto px-5 -mt-14 relative pb-20">
        {/* Avatar + identity */}
        <div className="flex items-end gap-4 mb-4">
          <div className="h-24 w-24 rounded-2xl border-4 border-background bg-muted overflow-hidden flex items-center justify-center flex-shrink-0">
            {p.avatar_url
              ? <img src={p.avatar_url} alt={p.display_name} className="h-full w-full object-cover" />
              : <Megaphone className="h-8 w-8 text-muted-foreground" />}
          </div>
          <div className="pb-1 min-w-0">
            <h1 className="text-2xl font-extrabold truncate" data-testid="promoter-name">{p.display_name}</h1>
            <p className="text-xs text-muted-foreground">@{p.handle} · <span data-testid="promoter-followers">{data.followers_count}</span> follower{data.followers_count === 1 ? '' : 's'} · Eventism Promoter</p>
          </div>
        </div>

        {p.bio && <p className="text-sm text-muted-foreground leading-relaxed mb-4 whitespace-pre-line" data-testid="promoter-bio">{p.bio}</p>}

        {/* Socials + follow */}
        <div className="flex flex-wrap items-center gap-2 mb-8">
          {user ? (
            <Button onClick={toggleFollow} disabled={working} data-testid="promoter-follow-btn"
              className={data.is_following ? 'bg-muted text-foreground hover:bg-muted/80' : 'bg-emerald-600 hover:bg-emerald-700'}>
              {working ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : data.is_following ? <Check className="h-4 w-4 mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
              {data.is_following ? 'Following' : 'Follow'}
            </Button>
          ) : emailFollowed ? (
            <Badge className="bg-emerald-500/15 text-emerald-400 py-2 px-3"><Check className="h-3.5 w-3.5 mr-1" /> Following by email</Badge>
          ) : (
            <div className="flex gap-2 w-full sm:w-auto">
              <Input type="email" value={followEmail} onChange={e => setFollowEmail(e.target.value)} placeholder="you@email.com" className="h-10 sm:w-56" data-testid="promoter-follow-email" />
              <Button onClick={toggleFollow} disabled={working} className="bg-emerald-600 hover:bg-emerald-700 h-10" data-testid="promoter-follow-btn">
                {working ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Follow'}
              </Button>
            </div>
          )}
          {socials.instagram && <a href={`https://instagram.com/${socials.instagram.replace(/^@|https?:\/\/(www\.)?instagram\.com\//, '')}`} target="_blank" rel="noreferrer" className="p-2.5 rounded-lg border border-border hover:bg-muted" data-testid="promoter-ig"><Instagram className="h-4 w-4" /></a>}
          {socials.website && <a href={socials.website.startsWith('http') ? socials.website : `https://${socials.website}`} target="_blank" rel="noreferrer" className="p-2.5 rounded-lg border border-border hover:bg-muted"><Globe className="h-4 w-4" /></a>}
        </div>

        {/* Share this page */}
        <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="promoter-share-row">
          <Button variant="outline" size="sm" onClick={sharePage} data-testid="promoter-share-btn">
            <Share2 className="h-3.5 w-3.5 mr-1.5" /> Share this page
          </Button>
          <a href={`mailto:?subject=${encodeURIComponent(`Follow ${p.display_name} on Eventism`)}&body=${encodeURIComponent(shareText)}`} className="p-2 rounded-lg border border-border hover:bg-muted" aria-label="Share by email" data-testid="promoter-share-email"><Mail className="h-4 w-4" /></a>
          <a href={`https://wa.me/?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noreferrer" className="p-2 rounded-lg border border-border hover:bg-muted" aria-label="Share on WhatsApp" data-testid="promoter-share-whatsapp"><MessageCircle className="h-4 w-4" /></a>
          <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noreferrer" className="p-2 rounded-lg border border-border hover:bg-muted" aria-label="Share on X" data-testid="promoter-share-x"><Twitter className="h-4 w-4" /></a>
          <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`} target="_blank" rel="noreferrer" className="p-2 rounded-lg border border-border hover:bg-muted" aria-label="Share on Facebook" data-testid="promoter-share-facebook"><Facebook className="h-4 w-4" /></a>
          <button type="button" onClick={copyPage} className="p-2 rounded-lg border border-border hover:bg-muted" aria-label="Copy link" data-testid="promoter-share-copy"><Copy className="h-4 w-4" /></button>
        </div>

        {/* Upcoming events */}
        <h2 className="text-base md:text-lg font-bold mb-3 flex items-center gap-2"><Ticket className="h-4 w-4 text-emerald-400" /> Upcoming events</h2>
        {data.promoting_now.length === 0 ? (
          <p className="text-sm text-muted-foreground mb-8">Nothing live right now — follow to be first to know.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4 mb-10" data-testid="promoting-now-grid">
            {data.promoting_now.map(ev => (
              <a key={ev.event_id} href={ev.tracked_url} className="group rounded-xl border border-border overflow-hidden hover:border-emerald-500/50 transition-colors" data-testid={`promo-event-${ev.event_id}`}>
                <div className="h-36 bg-muted overflow-hidden">
                  {ev.image_url && <img src={ev.image_url} alt={ev.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />}
                </div>
                <div className="p-3.5">
                  <p className="font-bold text-sm truncate">{ev.name}</p>
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Calendar className="h-3 w-3" /> {ev.event_date}{ev.event_time ? ` · ${ev.event_time}` : ''}</p>
                  {ev.venue_name && <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"><MapPin className="h-3 w-3" /> {ev.venue_name}</p>}
                  <span className="inline-block mt-2 text-xs font-bold text-emerald-400">Get tickets →</span>
                </div>
              </a>
            ))}
          </div>
        )}

        {/* Past events */}
        {data.past_events.length > 0 && (
          <>
            <h2 className="text-base md:text-lg font-bold mb-3 text-muted-foreground">Past events</h2>
            <div className="space-y-2" data-testid="past-events-list">
              {data.past_events.map(ev => (
                <Card key={ev.event_id} className="bg-muted/30">
                  <CardContent className="p-3 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-muted overflow-hidden flex-shrink-0">
                      {ev.image_url && <img src={ev.image_url} alt="" className="h-full w-full object-cover" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{ev.name}</p>
                      <p className="text-[11px] text-muted-foreground">{ev.event_date}{ev.venue_name ? ` · ${ev.venue_name}` : ''}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}

        <p className="text-[11px] text-muted-foreground text-center mt-12">
          Powered by <Link to="/" className="underline">Eventism</Link> · Want a promoter page like this? <Link to="/promo-partners" className="underline">Join the promo team</Link>
        </p>
      </div>
    </div>
  );
}
